import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';
import { BaseEntity } from './shared';

export const SLOT_TYPE_TRANSLATABLE_FIELDS = ['label'] as const;
export type SlotTypeTranslatableField = (typeof SLOT_TYPE_TRANSLATABLE_FIELDS)[number];
export type SlotTypeTranslations = TranslatableText<SlotTypeTranslatableField>;

/**
 * Behavior flags for a slot type definition.
 */
export interface SlotTypeBehaviorFlags {
  isBlocking?: boolean;
  isAllDay?: boolean;
  isReadOnlyInCalendar?: boolean;
  countsAgainstBalance?: boolean;
  supportsRecurrence?: boolean;
  /**
   * When true, absences of this type prevent the employee from being
   * assigned shifts in the planning solver on the days they are absent.
   * Falls back to `isBlocking` when not explicitly set (backward compat).
   */
  blocksPlanning?: boolean;
}

/**
 * Slot type definition response DTO.
 */
export interface SlotTypeDefinition extends BaseEntity {
  key: string;
  module: string;
  label: string;
  icon: string;
  color: string;
  category: string;
  behaviorFlags: SlotTypeBehaviorFlags;
  config: Record<string, unknown>;
  priority: number;
  isActive: boolean;
  isSystem: boolean;
  translations?: TranslatableText<string>;
}

/**
 * Slot type categories.
 */
export const slotTypeCategories = ['holiday', 'absence', 'location', 'custom'] as const;
export type SlotTypeCategory = (typeof slotTypeCategories)[number];

const categoryLiteral = "'holiday'|'absence'|'location'|'custom'";

/**
 * Query schema for filtering slot type definitions.
 */
export const slotTypeDefinitionQuerySchema = type({
  'category?': type(categoryLiteral).configure({
    description: "Restrict to one category: 'holiday', 'absence', 'location', or 'custom'.",
    examples: ['absence'],
  }),
  'module?': type('string').configure({
    description: 'Filter by the owning domain module (e.g. the library that registered the type).',
    examples: ['timetracking'],
  }),
  'isActive?': type('boolean').configure({
    description: 'When provided, filters to only active (`true`) or only deactivated (`false`) definitions.',
  }),
}).configure({ description: 'Query parameters for listing slot type definitions.' });
export type SlotTypeDefinitionQuery = typeof slotTypeDefinitionQuerySchema.t;

/**
 * Schema for creating a new slot type definition.
 */
export const createSlotTypeDefinitionSchema = type({
  key: type('string').configure({
    description: 'Stable machine-readable identifier, unique per tenant. Used in slot.data.type.',
    examples: ['vacation'],
  }),
  module: type('string').configure({
    description: 'Owning domain module that registered this definition.',
    examples: ['timetracking'],
  }),
  label: type('string').configure({
    description: 'Human-readable label shown in the UI.',
    examples: ['Vacation'],
  }),
  'icon?': type('string').configure({
    description: 'Material Icons identifier used on slot chips.',
    examples: ['beach_access'],
  }),
  'color?': type('string').configure({
    description: 'CSS color applied to slot chips. Six-digit hex recommended.',
    examples: ['#0ea5e9'],
  }),
  category: type(categoryLiteral).configure({
    description: "Semantic category: 'holiday', 'absence', 'location', or 'custom'.",
    examples: ['absence'],
  }),
  'behaviorFlags?': type('unknown').configure({
    description:
      'Feature flags controlling slot behaviour: isBlocking, isAllDay, isReadOnlyInCalendar, countsAgainstBalance, supportsRecurrence.',
  }),
  'config?': type('unknown').configure({
    description: 'Free-form per-type configuration (balance key, approval policy, etc.).',
  }),
  'priority?': type('number').configure({
    description: 'Sort order used in pickers. Lower numbers appear first.',
    examples: [10],
  }),
  'isActive?': type('boolean').configure({
    description: 'Whether the definition is offered in the UI. Defaults to `true` when omitted.',
  }),
  'translations?': translatableTextSchema(['label']),
}).configure({ description: 'Payload for creating a new slot type definition.' });
export type CreateSlotTypeDefinition = typeof createSlotTypeDefinitionSchema.t;

/**
 * Schema for updating a slot type definition.
 * System types have restricted editable fields (label, icon, color, isActive, priority).
 */
export const updateSlotTypeDefinitionSchema = type({
  'label?': type('string').configure({ description: 'Updated display label.', examples: ['Vacation'] }),
  'icon?': type('string').configure({ description: 'Updated Material Icons identifier.', examples: ['beach_access'] }),
  'color?': type('string').configure({ description: 'Updated CSS color (hex recommended).', examples: ['#0ea5e9'] }),
  'category?': type(categoryLiteral).configure({
    description: "Updated category: 'holiday', 'absence', 'location', or 'custom'. Ignored for system types.",
  }),
  'behaviorFlags?': type('unknown').configure({
    description: 'Updated behaviour flags. Ignored for system types.',
  }),
  'config?': type('unknown').configure({
    description: 'Updated per-type configuration. Ignored for system types.',
  }),
  'priority?': type('number').configure({ description: 'Updated sort priority.', examples: [10] }),
  'isActive?': type('boolean').configure({
    description: 'Activate (`true`) or deactivate (`false`) this definition.',
  }),
  'translations?': translatableTextSchema(['label']),
}).configure({
  description: 'Partial update for a slot type definition. System types only honour label, icon, color, priority, and isActive.',
});
export type UpdateSlotTypeDefinition = typeof updateSlotTypeDefinitionSchema.t;
